import type { App } from 'vue';

/**
 * Front-end errors to Sentry (2026-10-21, docs/monitoring.md), for the panel and the Mini App.
 *
 * VITE_SENTRY_DSN is baked in at build time (deploy.sh hands it to both builds); without it
 * nothing is loaded. The SDK comes in its own chunk after the first paint, so a page without a
 * DSN — staging, local — never downloads it. No user data: the browser SDK sends none by
 * default, and every URL loses its query and hash — the Mini App gets the player's Telegram data
 * in `#tgWebAppData`, a start parameter or a receipt code in the query.
 */
export function startErrorReporting(
    app: App,
    surface: 'panel' | 'miniapp',
): void {
    const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

    if (!dsn) {
        return;
    }

    void import('@sentry/vue').then((Sentry) => {
        Sentry.init({
            app,
            dsn,
            environment:
                (import.meta.env.VITE_SENTRY_ENVIRONMENT as
                    | string
                    | undefined) ?? 'production',
            initialScope: { tags: { surface } },
            beforeSend(event) {
                if (event.request) {
                    event.request.url = bareUrl(event.request.url);
                    delete event.request.query_string;
                }

                return event;
            },
            beforeBreadcrumb(crumb) {
                for (const key of ['url', 'from', 'to']) {
                    if (typeof crumb.data?.[key] === 'string') {
                        crumb.data[key] = bareUrl(crumb.data[key]);
                    }
                }

                return crumb;
            },
        });
    });
}

/** A URL without its query and hash. */
export function bareUrl<T extends string | undefined>(url: T): T {
    return (url === undefined ? url : url.split(/[?#]/)[0]) as T;
}
